import React, { useEffect, useState } from 'react';
import DeviceDataService from '../../../services/DeviceService';
import DeviceUsersService from '../../../services/DeviceUsersService';
import { makeStyles } from '@material-ui/core/styles';
import Attendance from './tables/Attendance';
import { CircularProgress } from '@material-ui/core';
import { Alert, AlertTitle } from '@material-ui/lab';
import { Autocomplete, Badge, Box, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles(theme => ({
    root: {
        flexGrow: 1,
        width: '100%',
        backgroundColor: theme.palette.background.paper,
    },
    picker: {
        padding: theme.spacing(2),
    },
}));

const DeviceUsersTable = () => {
    const { t } = useTranslation();
    const [loc] = React.useState(() => {
        const locData = localStorage.getItem('loc');
        return locData ? JSON.parse(locData).id : '';
    });

    const [devices, setDevice] = useState([]);

    useEffect(() => {
        retrieveDevices();
    }, []);

    const retrieveDevices = async () => {
        const locData = JSON.parse(localStorage.getItem('loc'));

        try {
            let response;

            if (Array.isArray(locData)) {
                const joinedValues = locData.join(',');
                response = await DeviceDataService.getAllNoEnroll(joinedValues);
            } else if (locData.id === 'all') {
                response = await DeviceDataService.adminAllNoEnroll();
            }

            if (response) {
                setDevice(response.data);
                console.log(response.data);
            }
        } catch (e) {
            console.log(e);
        }
    };

    //for error handling
    const [iserror, setIserror] = useState(false);
    const [errorMessages, setErrorMessages] = useState([]);

    const classes = useStyles();

    // * Selected device index
    const [value, setValue] = React.useState(0);

    useEffect(() => {
        if (devices.length && value >= devices.length) {
            setValue(0);
        }
    }, [devices, value]);

    function TabPanel({ value, index, devices }) {
        const [deviceUsers, setDeviceUsers] = useState([]);
        const [isLoading, setIsLoading] = useState(true);
        const [isError, setIsError] = useState(false);

        const retrieveDeviceUsers = async () => {
            setIsLoading(true);
            setIsError(false);

            // Get the current device
            const currentDevice = devices[index];
            if (!currentDevice) {
                setIsLoading(false);
                setIsError(true);
                console.error('No device found for index:', index);
                return;
            }

            try {
                console.log(
                    'Fetching users for device:',
                    currentDevice.name,
                    'ID:',
                    currentDevice.id
                );

                // Get location data for filtering
                const locData = JSON.parse(localStorage.getItem('loc'));
                let response;

                if (Array.isArray(locData)) {
                    const joinedValues = locData.join(',');
                    response = await DeviceUsersService.getByDevice(
                        currentDevice.id,
                        joinedValues
                    );
                } else if (locData.id === 'all') {
                    // For admin access, don't pass location filter
                    response = await DeviceUsersService.getByDevice(
                        currentDevice.id
                    );
                } else {
                    // Single location
                    response = await DeviceUsersService.getByDevice(
                        currentDevice.id,
                        locData.id
                    );
                }

                if (response && response.data) {
                    console.log(
                        'Device users response for',
                        currentDevice.name,
                        ':',
                        response.data
                    );
                    setDeviceUsers(response.data);
                } else {
                    console.warn(
                        'No data received for device:',
                        currentDevice.name
                    );
                    setDeviceUsers([]);
                }
            } catch (e) {
                setIsError(true);
                console.error(
                    'Error fetching device users for device:',
                    currentDevice.name,
                    e
                );
                setDeviceUsers([]);
            } finally {
                setIsLoading(false);
            }
        };

        useEffect(() => {
            if (
                devices &&
                devices.length > 0 &&
                index >= 0 &&
                index < devices.length
            ) {
                retrieveDeviceUsers();
            }
        }, [devices, index, value]); // Added value to re-fetch when tab changes

        const tab = devices[index];

        return (
            <div role="tabpanel" hidden={value !== index}>
                {value === index && (
                    <div>
                        {isLoading ? (
                            // Render the CircularProgress component while loading
                            <CircularProgress />
                        ) : isError ? (
                            // Render the Alert component for the error message
                            <Alert severity="error">
                                <AlertTitle>Error</AlertTitle>
                                Cannot load users for device{' '}
                                {(tab && tab.name) || 'Unknown'}
                            </Alert>
                        ) : (
                            // Render the Attendance component with the fetched data
                            <Attendance
                                deviceUsers={deviceUsers}
                                tabName={tab.name}
                                deviceMac={tab.mac}
                                deviceName={tab.name}
                                deviceId={tab.id}
                                retrieveDeviceUsers={retrieveDeviceUsers}
                            />
                        )}
                    </div>
                )}
            </div>
        );
    }

    const selectedDevice = devices[value] || null;

    return (
        <div className={classes.root}>
            <Box className={classes.picker}>
                <Autocomplete
                    options={devices}
                    value={selectedDevice}
                    getOptionLabel={opt => (opt && opt.name) || ''}
                    isOptionEqualToValue={(opt, val) =>
                        opt && val && opt.id === val.id
                    }
                    onChange={(event, newValue) => {
                        if (!newValue) return;
                        const idx = devices.findIndex(
                            d => d.id === newValue.id
                        );
                        if (idx >= 0) setValue(idx);
                    }}
                    disableClearable
                    openOnFocus
                    selectOnFocus
                    handleHomeEndKeys
                    renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                            <Badge
                                color={option.online ? 'success' : 'error'}
                                variant="dot"
                                sx={{ mr: 2 }}
                            />
                            {option.name}
                        </li>
                    )}
                    renderInput={params => (
                        <TextField
                            {...params}
                            variant="outlined"
                            size="small"
                            label={t('devices.devicePicker.label')}
                            placeholder={t(
                                'devices.devicePicker.searchPlaceholder'
                            )}
                        />
                    )}
                    size="small"
                    fullWidth
                />
            </Box>
            {selectedDevice && (
                <TabPanel
                    key={selectedDevice.id}
                    value={value}
                    index={value}
                    devices={devices}
                />
            )}
        </div>
    );
};

export default DeviceUsersTable;
